/**
 * Заключи… — placing the pickup's door from the order form (#420), or moving it.
 *
 * Opened on the pickup the dispatcher has in the box, with the address and the entrance read
 * from it and the map on the building. A click puts the door down; saving makes it one of the
 * firm's own addresses — final for an administrator, waiting for their approval for a
 * dispatcher — and the order's pickup moves to it.
 *
 * A pickup that already is one of the firm's addresses is moved rather than placed again: the
 * place is read whole first, so moving its door keeps its note, its names and its stop. One typed
 * rather than chosen is found when the save is refused as a duplicate, and offered instead —
 * taken as it is, or moved to the click.
 *
 * A pickup typed rather than chosen from the list has no point to open the map on. The
 * building is then looked up the way the desk's search finds it — the firm's own doors first —
 * and when nothing answers, the map opens on the firm's town to be panned from. It said
 * "choose from the list" and "click on the map" at once, with no map to click.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { request, resolveMapView } from '@taxi/api-client';

import { mapApi, type MapConfiguration } from '../map/api';
import type { Place } from '../orders/addresses';
import { geoApi } from '../orders/api';
import { describe } from '../settings/errors';
import { Banner, Button, Dialog, EmptyState, Field, Input } from '../ui';
import { draftOf, duplicateOf, notifyPlacesChanged, placesApi, type PlaceView } from './api';
import { readAddress } from './entrances';
import type { MapPoint } from './PlacesMap';

const PlacesMap = lazy(async () => ({ default: (await import('./PlacesMap')).PlacesMap }));

/** A door is placed close enough to see the block's entrances apart. */
const DOOR_ZOOM = 18;

/** The firm's town, when what was typed is not found: near enough to pan to the door. */
const TOWN_ZOOM = 13;

/** Where a map opens with nothing at all to go on, as the desk's does. */
const NOWHERE_IN_PARTICULAR = { longitude: 10, latitude: 50, zoom: 3 };

export function LockDialog({
  open,
  pickup,
  text,
  onClose,
  onPlaced,
}: {
  open: boolean;
  /** The pickup chosen, when one is — the point the map opens on, and the place to move. */
  pickup: Place | null;
  /** What is in the pickup box. */
  text: string;
  onClose: () => void;
  /** The place as saved, for the order form to take as its pickup. */
  onPlaced: (place: PlaceView) => void;
}) {
  const { t } = useTranslation();

  // The form is mounted afresh each time the dialog opens, so it starts from the pickup in the
  // box now rather than from the one it was last opened on.
  return (
    <Dialog
      open={open}
      wide
      title={pickup?.placeId != null ? t('places.lock.moveTitle') : t('places.lock.title')}
      onClose={onClose}
    >
      {open && <LockForm pickup={pickup} text={text} onClose={onClose} onPlaced={onPlaced} />}
    </Dialog>
  );
}

function LockForm({
  pickup,
  text,
  onClose,
  onPlaced,
}: {
  pickup: Place | null;
  text: string;
  onClose: () => void;
  onPlaced: (place: PlaceView) => void;
}) {
  const { t } = useTranslation();

  const [configuration, setConfiguration] = useState<MapConfiguration | null>(null);
  const [existing, setExisting] = useState<PlaceView | null>(null);
  /** The existing place came from a refused save rather than from the pickup chosen. */
  const [offered, setOffered] = useState(false);
  const [building, setBuilding] = useState(() => pickup?.firm?.building ?? readAddress(text).building);
  const [entrance, setEntrance] = useState(() => pickup?.firm?.entrance ?? readAddress(text).entrance ?? '');
  const [door, setDoor] = useState<MapPoint | null>(() =>
    pickup?.firm?.door ??
      (pickup?.placeId != null ? { longitude: pickup.longitude, latitude: pickup.latitude } : null),
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** Where the map opens for a typed pickup: the building found, or the firm's town. */
  const [looked, setLooked] = useState<{ longitude: number; latitude: number; zoom: number } | null>(null);
  /** The typed pickup was looked up, whatever came of it. */
  const [lookedUp, setLookedUp] = useState(false);

  useEffect(() => {
    let current = true;

    const configured = mapApi.configuration();

    void configured.then(
      (answer) => {
        if (current) setConfiguration(answer);
      },
      (failure: unknown) => {
        if (current) setProblem(describe(failure, t, 'settings.couldNotLoad'));
      },
    );

    if (pickup?.placeId != null) {
      void request<PlaceView>(`/api/places/${pickup.placeId}`).then(
        (place) => {
          if (!current) return;

          setExisting(place);
          setBuilding(place.building);
          setEntrance(place.entrance ?? '');
          setDoor(place.door);
        },
        () => undefined,
      );
    }

    if (pickup === null) {
      const typed = readAddress(text).building.trim();

      void (async () => {
        const found =
          typed.length === 0
            ? null
            : await geoApi.searchWidening(typed).then(
                (answer) => answer.results[0]?.location ?? null,
                () => null,
              );

        if (found !== null) {
          if (current) setLooked({ ...found, zoom: DOOR_ZOOM });
        } else {
          const [style, home] = await Promise.all([
            configured.catch(() => null),
            mapApi.home().catch(() => null),
          ]);

          if (current && style !== null) {
            setLooked(resolveMapView(home, style, { zoom: TOWN_ZOOM, nowhere: NOWHERE_IN_PARTICULAR }));
          }
        }

        if (current) setLookedUp(true);
      })();
    }

    return () => {
      current = false;
    };
  }, [pickup, text, t]);

  async function save() {
    if (door === null) {
      setProblem(t('places.lock.clickTheDoor'));
      return;
    }

    setBusy(true);
    setProblem(null);

    try {
      const draft = {
        ...(existing === null ? { names: [] } : draftOf(existing)),
        building: building.trim(),
        entrance: entrance.trim() || null,
        longitude: door.longitude,
        latitude: door.latitude,
      };

      const saved =
        existing === null ? await placesApi.add(draft) : await placesApi.update(existing.id, draft);

      notifyPlacesChanged();
      onPlaced(saved);
      onClose();
    } catch (failure) {
      const same = duplicateOf(failure);

      if (same !== null && existing === null) {
        await request<PlaceView>(`/api/places/${same}`).then(
          (place) => {
            setExisting(place);
            setOffered(true);
          },
          () => undefined,
        );
      }

      setProblem(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  const centre = door ?? (pickup === null ? null : { longitude: pickup.longitude, latitude: pickup.latitude });
  const view = centre !== null ? { ...centre, zoom: DOOR_ZOOM } : looked;

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-secondary text-ink-2">
        {existing === null ? t('places.lock.about') : t('places.lock.moveAbout')}
      </p>

      <div className="grid grid-cols-[1fr_96px] gap-3">
        <Field label={t('places.building')}>
          <Input value={building} onChange={(event) => setBuilding(event.target.value)} autoComplete="off" />
        </Field>
        <Field label={t('places.entrance')}>
          <Input
            value={entrance}
            maxLength={2}
            onChange={(event) => setEntrance(event.target.value.toLocaleUpperCase('bg'))}
            autoComplete="off"
          />
        </Field>
      </div>

      {problem !== null && <Banner tone="danger">{problem}</Banner>}

      {configuration === null || view === null ? (
        <EmptyState>
          {pickup === null && lookedUp && looked === null ? t('places.lock.noPoint') : t('app.loading')}
        </EmptyState>
      ) : (
        <Suspense fallback={<EmptyState>{t('app.loading')}</EmptyState>}>
          <PlacesMap
            configuration={configuration}
            view={view}
            pins={[]}
            picking="door"
            door={door}
            doorLabel={entrance.trim()}
            onPick={setDoor}
            className="h-[360px]"
          />
        </Suspense>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-secondary text-ink-3">{t('places.lock.clickTheDoor')}</span>
        <span className="flex gap-2">
          <Button onClick={onClose}>{t('settings.discard')}</Button>
          {offered && existing !== null && (
            <Button
              disabled={busy}
              onClick={() => {
                onPlaced(existing);
                onClose();
              }}
            >
              {t('places.lock.useIt')}
            </Button>
          )}
          <Button variant="primary" disabled={busy || building.trim().length === 0} onClick={() => void save()}>
            {busy ? t('settings.saving') : offered ? t('places.lock.moveHere') : t('places.lock.save')}
          </Button>
        </span>
      </div>
    </div>
  );
}
